import { createAction, type QueuedAction } from "@/v2/hooks/useActionQueue";
import { apiCall, type ActionDeps } from "./types";
import { durationToUnitsSeconds, formatEntryId } from "./entryId";

export type EditPayload = {
	id: number;
	date: string;
	matter: string;
	matter_id: string;
	description: string;
	duration: number;
	task: string;
	activity: string;
};

/**
 * Map the form payload to the v2 PATCH wire shape. Empty strings become null
 * (the v2 merge-patch convention for "clear this field"). `id` is in the URL,
 * not the body; `matter` is display-only and not sent.
 */
export function toUpdateBody(entry: EditPayload) {
	return {
		date: entry.date,
		unitsSeconds: durationToUnitsSeconds(entry.duration),
		description: entry.description,
		matterId: entry.matter_id || null,
		taskId: entry.task || null,
		activityId: entry.activity || null,
	};
}

export function editAction(entry: EditPayload, deps: ActionDeps): QueuedAction {
	const rollback = deps.updateEntries((prev) =>
		prev.map((e) => (e.id === entry.id ? { ...e, ...entry } : e)),
	);

	return createAction({
		execute: () =>
			apiCall(deps, {
				endpoint: `/v2/entries/${formatEntryId(entry.id)}`,
				method: "PATCH",
				body: toUpdateBody(entry),
			}),
		rollback,
		toast: {
			loading: "Saving changes…",
			success: "Entry saved",
			error: "Failed to save entry",
		},
		onSuccess: () => deps.refreshRecords(),
		onError: () => deps.refreshRecords(),
	});
}
